import React, { useState } from 'react';

interface TermChipsProps {
  /** Distinct pasted terms with their occurrence counts, already ordered for display. */
  chips: [string, number][];
  /** How many chips to show before the rest collapse behind "More …". */
  initialVisible?: number;
}

/**
 * Renders the pasted batch-search terms as chips. A large paste (hundreds of
 * PROs) would otherwise push the actual results far below the fold, so only the
 * first `initialVisible` chips show by default with a "More …" toggle that
 * expands the full list (and collapses it again).
 */
const TermChips: React.FC<TermChipsProps> = ({ chips, initialVisible = 10 }) => {
  const [expanded, setExpanded] = useState(false);

  if (chips.length === 0) return null;

  const hasMore = chips.length > initialVisible;
  const visible = expanded || !hasMore ? chips : chips.slice(0, initialVisible);
  const hiddenCount = chips.length - initialVisible;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {visible.map(([term, count]) => (
        <span
          key={term}
          title={count > 1 ? `Appears ${count}× in this search (shown once below)` : undefined}
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            count > 1
              ? 'bg-warn text-warn-fg ring-1 ring-amber-400 dark:ring-amber-600'
              : 'bg-gray-100 dark:bg-gray-700 text-label'
          }`}
        >
          {term}
          {count > 1 && <span className="ml-1 font-semibold">×{count}</span>}
        </span>
      ))}
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
        >
          {expanded ? 'Show less' : `More … (+${hiddenCount})`}
        </button>
      )}
    </div>
  );
};

export default TermChips;
